import type { Problem } from '@sportik/api-coach/problem';
import type { components } from '@sportik/api-coach/schema';
import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/services/api-client';

type ActivityList = components['schemas']['ActivityList'];
type Actor = components['schemas']['ActivityEntry']['actor'] | 'all';
type Cursor = string | undefined;

const PATH = '/clients/{clientId}/activity';

/**
 * The one definition of the timeline read: a client's activity, newest first,
 * a page at a time, polled every 30 s (ADR 0016 § 3). The Overview's preview
 * and the Activity tab share it. A change of filter keeps the entries shown
 * until the new ones arrive. The typed client has no options for a paged
 * read, so the pages are fetched here under the key it generates.
 */
export function activityOptions(clientId: string, actor: Actor = 'all') {
  const params = { path: { clientId }, query: { actor } };
  const { queryKey } = api.queryOptions('get', PATH, { params });
  // Every failure of the client is a `Problem`; the type says so here, as the
  // typed client's own reads do.
  return infiniteQueryOptions<
    ActivityList,
    Problem,
    InfiniteData<ActivityList, Cursor>,
    typeof queryKey,
    Cursor
  >({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, error } = await api.client.GET(PATH, {
        params: { ...params, query: { actor, cursor: pageParam } },
        signal,
      });
      if (error) throw error;
      return data;
    },
    initialPageParam: undefined,
    getNextPageParam: (page) => page.nextCursor,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Every variant of the timeline read: what a write that files an entry invalidates. */
export function activityKeyPrefix() {
  return activityOptions('').queryKey.slice(0, 2);
}

/** What the client and the coach did, newest first, with older pages on demand. */
export function useActivity(clientId: string, actor?: Actor) {
  return useInfiniteQuery(activityOptions(clientId, actor));
}
